import { supabase } from '@/integrations/supabase/client';

export interface ScrapedListingData {
  title?: string;
  description?: string;
  price?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareMeters?: string;
  address?: string;
  city?: string;
  features?: string[];
  images?: string[];
  listingType?: 'sale' | 'rent';
  propertyType?: string;
  condition?: 'new' | 'renovated' | 'good' | 'needs-renovation';
  furnished?: 'fully' | 'partially' | 'unfurnished';
  currency?: string;
}

export interface ScrapeResult {
  success: boolean;
  data?: ScrapedListingData;
  error?: string;
   isNotRealEstate?: boolean;
   detectedType?: string;
}

 // Validation result interface
 export interface ValidationResult {
   success: boolean;
   isRealEstate: boolean;
   confidence: 'high' | 'medium' | 'low';
   detectedType?: string;
   reason?: string;
   message?: string;
 }
 
 /**
  * Validate if the URL is a real estate listing before full scraping
  * This prevents wasting time/resources on non-real-estate URLs
  */
 async function validateListingUrl(url: string): Promise<ValidationResult> {
   try {
     console.log('🔍 Validating URL for real estate content...');
     
     const { data, error } = await supabase.functions.invoke('validate-listing-url', {
       body: { url },
     });
 
     if (error) {
       console.warn('URL validation failed, proceeding with import:', error);
       // On error, allow through with low confidence
       return { success: true, isRealEstate: true, confidence: 'low' };
     }
 
     console.log('Validation result:', data);
     return {
       success: data?.success ?? true,
       isRealEstate: data?.isRealEstate ?? true,
       confidence: data?.confidence ?? 'low',
       detectedType: data?.detectedType,
       reason: data?.reason,
       message: data?.message,
     };
   } catch (err) {
     console.warn('URL validation error, proceeding with import:', err);
     return { success: true, isRealEstate: true, confidence: 'low' };
   }
 }

// Extract Open Graph and meta tags from HTML
function extractMetaTags(html: string): Record<string, string> {
  const metaTags: Record<string, string> = {};
  
  // Open Graph tags
  const ogRegex = /<meta[^>]*property=["']og:([^"']+)["'][^>]*content=["']([^"']*)["'][^>]*>/gi;
  let match;
  while ((match = ogRegex.exec(html)) !== null) {
    metaTags[`og:${match[1]}`] = match[2];
  }
  
  // Also check reversed attribute order
  const ogRegex2 = /<meta[^>]*content=["']([^"']*)["'][^>]*property=["']og:([^"']+)["'][^>]*>/gi;
  while ((match = ogRegex2.exec(html)) !== null) {
    metaTags[`og:${match[2]}`] = match[1];
  }
  
  // Standard meta tags
  const metaRegex = /<meta[^>]*name=["']([^"']+)["'][^>]*content=["']([^"']*)["'][^>]*>/gi;
  while ((match = metaRegex.exec(html)) !== null) {
    metaTags[match[1]] = match[2];
  }
  
  // Title tag
  const titleMatch = html.match(/<title[^>]*>([^<]*)<\/title>/i);
  if (titleMatch) {
    metaTags['title'] = titleMatch[1].trim();
  }
  
  return metaTags;
}

// Extract images from HTML (PRIMARY METHOD - do not modify)
function extractImages(html: string, baseUrl: string): string[] {
  const images: string[] = [];
  const imgRegex = /<img[^>]*src=["']([^"']+)["'][^>]*>/gi;
  let match;
  
  while ((match = imgRegex.exec(html)) !== null) {
    let src = match[1];
    
    // Skip small images, icons, logos, thumbnails, etc.
    // BUT allow CDN-hosted property images (they often have generic patterns)
    const isCdnPropertyImage = /cdn\.|cloudfront|imgix|cloudinary|staycaee|assets\..*\/properties|uploads\/.*property/i.test(src);
    
    if (!isCdnPropertyImage && (
        src.includes('logo') || 
        src.includes('icon') || 
        src.includes('avatar') ||
        src.includes('placeholder') ||
        src.includes('thumb') ||
        src.includes('_small') ||
        src.includes('_xs') ||
        src.includes('_s.') ||
        src.includes('/s/') ||
        src.includes('1x1') ||
        src.includes('pixel') ||
        src.includes('sprite') ||
        src.includes('loading') ||
        src.includes('spinner') ||
        src.endsWith('.svg') ||
        src.endsWith('.gif'))) {
      continue;
    }
    
    // Make relative URLs absolute
    if (src.startsWith('//')) {
      src = 'https:' + src;
    } else if (src.startsWith('/')) {
      try {
        const url = new URL(baseUrl);
        src = url.origin + src;
      } catch { continue; }
    } else if (!src.startsWith('http')) {
      try {
        src = new URL(src, baseUrl).href;
      } catch { continue; }
    }
    
    if (!images.includes(src)) {
      images.push(src);
    }
  }
  
  // Also extract from data-src (lazy loading) but skip small variants
  const dataSrcRegex = /data-src=["']([^"']+)["']/gi;
  while ((match = dataSrcRegex.exec(html)) !== null) {
    let src = match[1];
    if (src.includes('thumb') || src.includes('_small') || src.includes('_xs')) continue;
    if (src.startsWith('//')) src = 'https:' + src;
    else if (src.startsWith('/')) {
      try {
        const url = new URL(baseUrl);
        src = url.origin + src;
      } catch { continue; }
    }
    if (src.startsWith('http') && !images.includes(src)) {
      images.push(src);
    }
  }
  
  // Also extract CDN image URLs directly from HTML (for SPAs that embed URLs in scripts/data)
  const cdnPatterns = [
    /https?:\/\/cdn\.staycaee\.com\/[^"'\s\)]+\.(?:webp|jpg|jpeg|png)/gi,
    /https?:\/\/[^"'\s]*cloudfront\.net\/[^"'\s]+\.(?:webp|jpg|jpeg|png)/gi,
    /https?:\/\/[^"'\s]*imgix\.net\/[^"'\s]+\.(?:webp|jpg|jpeg|png)/gi,
    /https?:\/\/res\.cloudinary\.com\/[^"'\s]+\.(?:webp|jpg|jpeg|png)/gi,
  ];
  
  for (const pattern of cdnPatterns) {
    let cdnMatch;
    while ((cdnMatch = pattern.exec(html)) !== null) {
      const cdnUrl = cdnMatch[0];
      // Skip floorplans, icons, logos
      if (!/floorplan|icon|logo|avatar|thumb/i.test(cdnUrl) && !images.includes(cdnUrl)) {
        images.push(cdnUrl);
      }
    }
  }
  
  // Deduplicate by normalizing URLs (remove size parameters)
  const normalizeUrl = (url: string): string => {
    try {
      const u = new URL(url);
      ['w', 'h', 'width', 'height', 'size', 'resize', 'fit', 'crop', 'quality', 'q'].forEach(param => {
        u.searchParams.delete(param);
      });
      return u.toString().replace(/\/$/, '');
    } catch {
      return url;
    }
  };
  
  const seen = new Set<string>();
  const uniqueImages = images.filter(img => {
    const normalized = normalizeUrl(img);
    const basePath = img.replace(/[-_]\d+x\d+/g, '').replace(/[-_]?\d{3,4}(?:x\d{3,4})?/g, '');
    const key = normalized + '|' + basePath;
    if (seen.has(key) || seen.has(normalized) || seen.has(basePath)) {
      return false;
    }
    seen.add(key);
    seen.add(normalized);
    seen.add(basePath);
    return true;
  });
  
  return uniqueImages.slice(0, 30);
}

// Extract text content from HTML
function extractTextContent(html: string): string {
  let cleaned = html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '')
    .replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
    .replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '');
  
  cleaned = cleaned.replace(/<[^>]+>/g, ' ');
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  cleaned = cleaned.replace(/&amp;/g, '&');
  cleaned = cleaned.replace(/&lt;/g, '<');
  cleaned = cleaned.replace(/&gt;/g, '>');
  cleaned = cleaned.replace(/&quot;/g, '"');
  cleaned = cleaned.replace(/&#39;/g, "'");
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  return cleaned.trim();
}

// Parse price from text - prioritize TOTAL price over price per sqm
function parsePrice(text: string, squareMeters?: string): string | undefined {
  // ENHANCED: Collect all prices and find the best TOTAL price
  const allPriceMatches: Array<{ value: number; isPricePerSqm: boolean; raw: string; context: string }> = [];
  
  // Match prices with € symbol (both formats: €185,000 and 185,000€)
  const priceRegex = /€\s*([\d,\.]+(?:[,\.]\d{3})*)|(\d[\d,\.]*)\s*€/gi;
  let match;
  while ((match = priceRegex.exec(text)) !== null) {
    const rawPrice = match[1] || match[2];
    // Parse price - handle European (1.234.567) and US (1,234,567) formats
    let priceValue: number;
    if (rawPrice.includes('.') && rawPrice.includes(',')) {
      // Mixed format - last separator is decimal
      const lastDot = rawPrice.lastIndexOf('.');
      const lastComma = rawPrice.lastIndexOf(',');
      if (lastDot > lastComma) {
        // European format: 1.234.567,89 or 1,234,567.89
        priceValue = parseFloat(rawPrice.replace(/,/g, '').replace(/\.(?=.*\.)/g, ''));
      } else {
        priceValue = parseFloat(rawPrice.replace(/\./g, '').replace(',', '.'));
      }
    } else if (rawPrice.includes(',') && rawPrice.split(',').pop()!.length <= 2) {
      // Decimal comma: 185000,50
      priceValue = parseFloat(rawPrice.replace(',', '.'));
    } else {
      // Standard format or thousands separator only
      priceValue = parseInt(rawPrice.replace(/[,\.]/g, ''), 10);
    }
    
    // Check context for price-per-sqm indicators - look for /m² before AND after the price
    const contextStart = Math.max(0, match.index - 80);
    const contextEnd = Math.min(text.length, match.index + match[0].length + 80);
    const context = text.substring(contextStart, contextEnd).toLowerCase();
    
    // More robust per-sqm detection patterns
    const perSqmPatterns = [
      /\/\s*(?:m²|m2|sqm|sq\.?\s*m|τ\.?μ\.?)/i,
      /per\s*(?:m²|m2|sqm|sq\.?\s*m|τ\.?μ\.?)/i,
      /ανά\s*(?:m²|m2|τ\.?μ\.?)/i,
      /(?:m²|m2|sqm)\s*price/i,
      /price\s*(?:per|\/)\s*(?:m²|m2|sqm)/i,
      /τιμή\s*(?:ανά|\/)\s*τ\.?μ\.?/i,
      /\(€?\d+[,\.]?\d*\s*\/\s*m/i, // Pattern like (€1,850/m²)
    ];
    
    const hasSqmIndicator = perSqmPatterns.some(p => p.test(context));
    
    // Also check if price is suspiciously low for a property (likely per sqm)
    // €500-€15,000 range is typically price per sqm, not total
    const likelyPerSqm = priceValue >= 500 && priceValue <= 15000 && !context.includes('rent') && !context.includes('month');
    
    if (priceValue > 0 && !isNaN(priceValue)) {
      allPriceMatches.push({
        value: priceValue,
        isPricePerSqm: hasSqmIndicator || (likelyPerSqm && allPriceMatches.some(p => p.value > 50000)),
        raw: rawPrice,
        context: context.substring(0, 100)
      });
    }
  }
  
  console.log('All price matches found:', allPriceMatches.map(p => ({
    value: p.value,
    isPricePerSqm: p.isPricePerSqm,
    context: p.context.substring(0, 50)
  })));
  
  // First, try to find a total/asking price (labeled explicitly)
  const totalPricePatterns = [
    /(?:total|full|asking|sale|list)\s*(?:price)?:?\s*€?\s*([\d,\.]+)/i,
    /(?:price|τιμή|συνολική):?\s*€?\s*([\d,\.]+)/i,
    /€\s*([\d,\.]+)\s*(?:total|asking|for\s*sale)/i,
  ];
  
  for (const pattern of totalPricePatterns) {
    const match = text.match(pattern);
    if (match) {
      const price = match[1].replace(/[,\.]/g, '').replace(/\D/g, '');
      const numPrice = parseInt(price, 10);
      if (numPrice > 30000) { // Likely a valid total price for sale
        console.log(`Found labeled total price: ${numPrice}`);
        return price;
      }
    }
  }
  
  // Separate prices into likely-total vs likely-per-sqm
  const totalPrices = allPriceMatches.filter(p => !p.isPricePerSqm && p.value > 30000);
  const perSqmPrices = allPriceMatches.filter(p => p.isPricePerSqm || (p.value >= 500 && p.value <= 15000));
  
  // Prefer the largest total price
  if (totalPrices.length > 0) {
    const bestTotal = totalPrices.reduce((a, b) => a.value > b.value ? a : b);
    console.log(`Selected total price: ${bestTotal.value} (from ${totalPrices.length} candidates)`);
    return String(bestTotal.value);
  }
  
  // If we have sqm and per-sqm price, calculate total
  if (perSqmPrices.length > 0 && squareMeters) {
    const sqm = parseFloat(squareMeters);
    if (sqm > 0) {
      const pricePerSqm = perSqmPrices[0];
      const calculatedTotal = Math.round(pricePerSqm.value * sqm);
      console.log(`Calculated total from price/sqm: ${pricePerSqm.value} x ${sqm} = ${calculatedTotal}`);
      return String(calculatedTotal);
    }
  }
  
  // Fall back to the largest price found (but only if reasonable for a property)
  if (allPriceMatches.length > 0) {
    const sortedByValue = [...allPriceMatches].sort((a, b) => b.value - a.value);
    // Take largest that's not obviously per-sqm
    const bestCandidate = sortedByValue.find(p => p.value > 20000) || sortedByValue[0];
    console.log(`Fallback to largest price: ${bestCandidate.value}`);
    return String(bestCandidate.value);
  }
  
  // Final fallback: try any EUR/$/£ patterns
  const fallbackPatterns = [
    /EUR\s*([\d,\.]+)/i,
    /([\d,\.]+)\s*EUR/i,
    /\$\s*([\d,\.]+)/,
    /([\d,\.]+)\s*\$/,
    /£\s*([\d,\.]+)/,
  ];
  
  for (const pattern of fallbackPatterns) {
    const match = text.match(pattern);
    if (match) {
      const price = match[1].replace(/[,\.]/g, '').replace(/\D/g, '');
      return price;
    }
  }
  
  return undefined;
}

// Parse bedrooms from text
function parseBedrooms(text: string): number | undefined {
  const patterns = [
    /(\d+)\s*(?:bed(?:room)?s?|br|bd)/i,
    /(?:bed(?:room)?s?|br|bd):\s*(\d+)/i,
    /(\d+)\s*υπνοδωμάτι/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const num = parseInt(match[1]);
      if (num >= 0 && num <= 20) return num;
    }
  }
  return undefined;
}

// Parse bathrooms from text
function parseBathrooms(text: string): number | undefined {
  const patterns = [
    /(\d+(?:\.\d)?)\s*(?:bath(?:room)?s?|ba)/i,
    /(?:bath(?:room)?s?|ba):\s*(\d+(?:\.\d)?)/i,
    /(\d+)\s*μπάνι/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const num = parseFloat(match[1]);
      if (num >= 0 && num <= 20) return num;
    }
  }
  return undefined;
}

// Parse square meters from text
function parseSquareMeters(text: string): string | undefined {
  const patterns = [
    /(\d+(?:[,\.]\d+)?)\s*(?:sq\.?\s*m|m²|m2|sqm|square\s*met)/i,
    /(?:sq\.?\s*m|m²|m2|sqm|area|size):\s*(\d+(?:[,\.]\d+)?)/i,
    /(\d+(?:[,\.]\d+)?)\s*τ\.?μ\.?/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1].replace(',', '.');
    }
  }
  return undefined;
}

// Detect listing type
function detectListingType(text: string): 'sale' | 'rent' | undefined {
  const lowerText = text.toLowerCase();
  
  const rentPatterns = [/for\s*rent/i, /to\s*rent/i, /rental/i, /ενοικίαση/i, /per\s*month/i, /\/\s*month/i, /monthly/i];
  const salePatterns = [/for\s*sale/i, /to\s*buy/i, /πώληση/i, /πωλείται/i];
  
  for (const pattern of rentPatterns) {
    if (pattern.test(lowerText)) return 'rent';
  }
  for (const pattern of salePatterns) {
    if (pattern.test(lowerText)) return 'sale';
  }
  return undefined;
}

// Detect property condition from text
function detectCondition(text: string): 'new' | 'renovated' | 'good' | 'needs-renovation' | undefined {
  const lowerText = text.toLowerCase();
  
  // New build patterns
  const newPatterns = [
    /\bnew\s*build\b/i, /\bnewly\s*built\b/i, /\bbrand\s*new\b/i,
    /\bnew\s*construction\b/i, /\bnew\s*development\b/i, /\bunder\s*construction\b/i,
    /\boff\s*plan\b/i, /\boff-plan\b/i, /\bνεόδμητο\b/i, /\bνέα\s*κατασκευή\b/i,
    /\bnew\s*project\b/i, /\bjust\s*completed\b/i, /\brecently\s*completed\b/i,
    /\b202[3-9]\s*build\b/i, /\bbuilt\s*in\s*202[3-9]\b/i
  ];
  
  // Renovated patterns
  const renovatedPatterns = [
    /\brenovated\b/i, /\brefurbished\b/i, /\bremodeled\b/i, /\bremodelled\b/i,
    /\brestored\b/i, /\bmodernized\b/i, /\bmodernised\b/i, /\bupgraded\b/i,
    /\bfully\s*renovated\b/i, /\brecently\s*renovated\b/i, /\bnewly\s*renovated\b/i,
    /\bανακαινισμένο\b/i, /\bανακαίνιση\b/i, /\bπλήρως\s*ανακαινισμένο\b/i
  ];
  
  // Needs renovation patterns
  const needsRenovationPatterns = [
    /\bneeds\s*renovati/i, /\brequires\s*renovati/i, /\bneeds\s*work\b/i,
    /\bneeds\s*updating\b/i, /\bfixer\s*upper\b/i, /\bto\s*renovate\b/i,
    /\bfor\s*renovation\b/i, /\brenovation\s*project\b/i, /\bproject\s*property\b/i,
    /\bχρειάζεται\s*ανακαίνιση\b/i, /\bπρος\s*ανακαίνιση\b/i
  ];
  
  // Good condition patterns - includes 'resale' and 'second-hand' which indicate used but functional properties
  const goodPatterns = [
    /\bgood\s*condition\b/i, /\bexcellent\s*condition\b/i, /\bvery\s*good\s*condition\b/i,
    /\bwell\s*maintained\b/i, /\bwell-maintained\b/i, /\bmove[\s-]?in\s*ready\b/i,
    /\bgreat\s*condition\b/i, /\bpristine\b/i, /\bimmaculate\b/i,
    /\bκαλή\s*κατάσταση\b/i, /\bάριστη\s*κατάσταση\b/i,
    /\bresale\b/i, /\bsecond[\s-]?hand\b/i, /\bused\b/i, /\bexisting\b/i
  ];
  
  // Check in order of specificity
  for (const pattern of newPatterns) {
    if (pattern.test(lowerText)) return 'new';
  }
  for (const pattern of renovatedPatterns) {
    if (pattern.test(lowerText)) return 'renovated';
  }
  for (const pattern of needsRenovationPatterns) {
    if (pattern.test(lowerText)) return 'needs-renovation';
  }
  for (const pattern of goodPatterns) {
    if (pattern.test(lowerText)) return 'good';
  }
  
  return undefined;
}

// Detect city from text content - supports worldwide cities
function detectCity(text: string): string | undefined {
  // Common words that should NOT be treated as cities
  const cityExclusions = /^(the|this|our|your|sale|rent|buy|new|old|great|nice|beautiful|luxury|modern|central|prime|main|best|good|excellent|perfect|amazing|stunning|spacious|cozy|quiet|peaceful|exclusive|premium|unique|rare|featured|available|immediate|urgent|reduced|price|area|zone|district|center|centre|downtown|suburb|north|south|east|west|upper|lower|near|close|next|opposite|behind|front|back|property|apartment|house|villa|home|flat|bedroom|bathroom|kitchen|living|floor|ground|first|second|third|pool|garden|parking|garage|terrace|balcony|sea|mountain|city|view|views)$/i;
  
  // Try to extract from labeled fields first (most reliable)
  const labeledPatterns = [
    /(?:^|\n|[|>])\s*city\s*:?\s*\[?([A-Za-zÀ-ÿ\u0370-\u03FF\u0400-\u04FF\s\-']+?)\]?(?:\s*[,|\n]|$)/im,
    /(?:^|\n|[|>])\s*location\s*:?\s*\[?([A-Za-zÀ-ÿ\u0370-\u03FF\u0400-\u04FF\s\-']+?)\]?(?:\s*[,|\n]|$)/im,
    /(?:^|\n|[|>])\s*area\s*:?\s*\[?([A-Za-zÀ-ÿ\u0370-\u03FF\u0400-\u04FF\s\-']+?)\]?(?:\s*[,|\n]|$)/im,
    /(?:property|apartment|house|villa|home|flat)\s+(?:in|at)\s+([A-Za-zÀ-ÿ\u0370-\u03FF\s\-']+?)(?:\s*[,.|]|$)/im,
    /(?:for\s+(?:sale|rent))\s+in\s+([A-Za-zÀ-ÿ\u0370-\u03FF\s\-']+?)(?:\s*[,.|]|$)/im,
  ];
  
  for (const pattern of labeledPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const cityCandidate = match[1].trim().split(/[,|]/)[0].trim();
      if (cityCandidate.length >= 2 && cityCandidate.length <= 50 && 
          !cityExclusions.test(cityCandidate) && !/^\d+$/.test(cityCandidate)) {
        return cityCandidate;
      }
    }
  }
  
  // Check for known cities
  const knownCities: Array<{ pattern: RegExp; city: string }> = [
    // Cyprus cities
    { pattern: /\b(nicosia|λευκωσία|lefkosia)\b/i, city: 'Nicosia' },
    { pattern: /\b(limassol|λεμεσός|lemesos)\b/i, city: 'Limassol' },
    { pattern: /\b(larnaca|λάρνακα|larnaka)\b/i, city: 'Larnaca' },
    { pattern: /\b(paphos|πάφος|pafos)\b/i, city: 'Paphos' },
    { pattern: /\b(famagusta|αμμόχωστος)\b/i, city: 'Famagusta' },
    { pattern: /\b(paralimni|παραλίμνι)\b/i, city: 'Paralimni' },
    { pattern: /\b(ayia\s*napa|αγία\s*νάπα)\b/i, city: 'Ayia Napa' },
    { pattern: /\b(protaras|πρωταράς)\b/i, city: 'Protaras' },
    { pattern: /\b(polis|πόλη)\b/i, city: 'Polis' },
    { pattern: /\b(strovolos|στρόβολος)\b/i, city: 'Strovolos' },
    { pattern: /\b(germasogeia|γερμασόγεια)\b/i, city: 'Germasogeia' },
    // Major European cities
    { pattern: /\b(london)\b/i, city: 'London' },
    { pattern: /\b(paris)\b/i, city: 'Paris' },
    { pattern: /\b(berlin)\b/i, city: 'Berlin' },
    { pattern: /\b(madrid)\b/i, city: 'Madrid' },
    { pattern: /\b(barcelona)\b/i, city: 'Barcelona' },
    { pattern: /\b(rome|roma)\b/i, city: 'Rome' },
    { pattern: /\b(milan|milano)\b/i, city: 'Milan' },
    { pattern: /\b(amsterdam)\b/i, city: 'Amsterdam' },
    { pattern: /\b(vienna|wien)\b/i, city: 'Vienna' },
    { pattern: /\b(munich|münchen)\b/i, city: 'Munich' },
    { pattern: /\b(lisbon|lisboa)\b/i, city: 'Lisbon' },
    { pattern: /\b(athens|αθήνα)\b/i, city: 'Athens' },
    { pattern: /\b(dublin)\b/i, city: 'Dublin' },
    { pattern: /\b(brussels|bruxelles)\b/i, city: 'Brussels' },
    { pattern: /\b(prague|praha)\b/i, city: 'Prague' },
    // Middle East
    { pattern: /\b(dubai|دبي)\b/i, city: 'Dubai' },
    { pattern: /\b(abu\s*dhabi)\b/i, city: 'Abu Dhabi' },
    { pattern: /\b(tel\s*aviv)\b/i, city: 'Tel Aviv' },
    { pattern: /\b(istanbul)\b/i, city: 'Istanbul' },
    // Americas
    { pattern: /\b(new\s*york)\b/i, city: 'New York' },
    { pattern: /\b(los\s*angeles)\b/i, city: 'Los Angeles' },
    { pattern: /\b(miami)\b/i, city: 'Miami' },
    { pattern: /\b(toronto)\b/i, city: 'Toronto' },
    // Asia-Pacific
    { pattern: /\b(singapore)\b/i, city: 'Singapore' },
    { pattern: /\b(hong\s*kong)\b/i, city: 'Hong Kong' },
    { pattern: /\b(tokyo)\b/i, city: 'Tokyo' },
    { pattern: /\b(sydney)\b/i, city: 'Sydney' },
    // Greece
    { pattern: /\b(thessaloniki|θεσσαλονίκη)\b/i, city: 'Thessaloniki' },
  ];
  
  for (const { pattern, city } of knownCities) {
    if (pattern.test(text)) {
      return city;
    }
  }
  
  return undefined;
}

// Detect property type from text - check title patterns first for most accurate match
function detectPropertyType(text: string): { type: string; category: 'residential' | 'commercial' | 'land' } | undefined {
  const lowerText = text.toLowerCase();
  
  // Order is important - most specific types first to avoid false positives
  const residentialTypes = [
    // Very specific types first
    { pattern: /\b(penthouse|πεντχάουζ)\b/i, type: 'penthouse' },
    { pattern: /\b(luxury\s*villa|βίλα|βίλλα|villa)\b/i, type: 'villa' },
    { pattern: /\b(maisonette|μεζονέτα|maisonnette|duplex|ντούπλεξ|split\s*level)\b/i, type: 'maisonette' },
    { pattern: /\b(townhouse|town\s*house|terraced)\b/i, type: 'townhouse' },
    { pattern: /\b(bungalow|μπανγκαλόου)\b/i, type: 'house' },
    { pattern: /\b(semi[\s-]?detached)\b/i, type: 'house' },
    { pattern: /\b(studio|στούντιο|γκαρσονιέρα|bedsit)\b/i, type: 'studio' },
    // Generic residential after specific
    { pattern: /\b(apartment|flat|διαμέρισμα|floor\s*apartment)\b/i, type: 'apartment' },
    { pattern: /\b(detached\s*house|detached\s*home)\b/i, type: 'house' },
    { pattern: /\b(house|home|σπίτι|κατοικία|οικία|residence)\b/i, type: 'house' },
  ];
  
  const commercialTypes = [
    { pattern: /\b(office|γραφείο|γραφεία|offices|office\s*space)\b/i, type: 'office' },
    { pattern: /\b(shop|κατάστημα|μαγαζί|retail\s*unit|retail\s*space|store)\b/i, type: 'shop' },
    { pattern: /\b(warehouse|αποθήκη|industrial\s*unit)\b/i, type: 'warehouse' },
    { pattern: /\b(restaurant|εστιατόριο|cafe|καφετέρια)\b/i, type: 'restaurant' },
    { pattern: /\b(hotel|ξενοδοχείο|boutique\s*hotel)\b/i, type: 'hotel' },
    { pattern: /\b(commercial\s*building|κτίριο)\b/i, type: 'building' },
    { pattern: /\b(showroom)\b/i, type: 'shop' },
  ];
  
  const landTypes = [
    { pattern: /\b(building\s*plot|residential\s*plot)\b/i, type: 'residential-plot' },
    { pattern: /\b(commercial\s*plot|commercial\s*land|εμπορικό\s*οικόπεδο)\b/i, type: 'commercial-plot' },
    { pattern: /\b(agricultural|αγροτικό|αγροτεμάχιο|farm|farmland|χωράφι)\b/i, type: 'agricultural' },
    { pattern: /\b(industrial\s*land|βιομηχανικό)\b/i, type: 'industrial' },
    // Generic land last
    { pattern: /\b(plot|land|οικόπεδο)\b/i, type: 'residential-plot' },
  ];
  
  for (const { pattern, type } of residentialTypes) {
    if (pattern.test(lowerText)) {
      return { type, category: 'residential' };
    }
  }
  for (const { pattern, type } of commercialTypes) {
    if (pattern.test(lowerText)) {
      return { type, category: 'commercial' };
    }
  }
  for (const { pattern, type } of landTypes) {
    if (pattern.test(lowerText)) {
      return { type, category: 'land' };
    }
  }
  
  return undefined;
}

// Detect features from text content
function detectFeatures(text: string): string[] {
  const lowerText = text.toLowerCase();
  const features: string[] = [];
  
  const featurePatterns: Array<{ pattern: RegExp; feature: string }> = [
    // Climate
    { pattern: /\b(a\/c|ac\b|air[\s-]?conditioning|aircon|split\s*unit|κλιματισμός)\b/i, feature: 'air-conditioning' },
    { pattern: /\b(central\s*heating|κεντρική\s*θέρμανση|radiators)\b/i, feature: 'central-heating' },
    { pattern: /\b(underfloor\s*heating|floor\s*heating|heated\s*floors)\b/i, feature: 'underfloor-heating' },
    { pattern: /\b(fireplace|τζάκι|open\s*fire)\b/i, feature: 'fireplace' },
    { pattern: /\b(heat\s*pump)\b/i, feature: 'heat-pump' },
    // Pool
    { pattern: /\b(private\s*pool|swimming\s*pool|infinity\s*pool|πισίνα)(?!\s*communal|\s*shared)/i, feature: 'pool' },
    { pattern: /\b(communal\s*pool|shared\s*pool|common\s*pool)\b/i, feature: 'communal-pool' },
    // Outdoor
    { pattern: /\b(garden|κήπος|private\s*garden)\b/i, feature: 'garden' },
    { pattern: /\b(balcony|μπαλκόνι)\b/i, feature: 'balcony' },
    { pattern: /\b(terrace|veranda|βεράντα|patio)\b/i, feature: 'terrace' },
    { pattern: /\b(rooftop|roof\s*terrace)\b/i, feature: 'rooftop' },
    { pattern: /\b(bbq|barbecue|ψησταριά)\b/i, feature: 'bbq-area' },
    // Views
    { pattern: /\b(sea\s*view|seaview|ocean\s*view|θέα\s*θάλασσα)\b/i, feature: 'sea-view' },
    { pattern: /\b(mountain\s*view|θέα\s*βουνό)\b/i, feature: 'mountain-view' },
    { pattern: /\b(city\s*view|panoramic\s*view)\b/i, feature: 'city-view' },
    // Building
    { pattern: /\b(elevator|lift|ασανσέρ|ανελκυστήρας)\b/i, feature: 'elevator' },
    { pattern: /\b(gym|fitness|γυμναστήριο)\b/i, feature: 'gym' },
    { pattern: /\b(smart\s*home|home\s*automation)\b/i, feature: 'smart-home' },
    { pattern: /\b(en[\s-]?suite|ensuite|master\s*bathroom)\b/i, feature: 'en-suite' },
    { pattern: /\b(guest\s*wc|guest\s*toilet|powder\s*room)\b/i, feature: 'guest-wc' },
    { pattern: /\b(maid'?s?\s*room|staff\s*room)\b/i, feature: 'maids-room' },
    { pattern: /\b(storage|storeroom|αποθήκη)\b/i, feature: 'storage' },
    { pattern: /\b(laundry\s*room)\b/i, feature: 'laundry-room' },
    { pattern: /\b(walk[\s-]?in\s*closet|dressing\s*room)\b/i, feature: 'walk-in-closet' },
    // Wellness
    { pattern: /\b(jacuzzi|hot\s*tub|υδρομασάζ)\b/i, feature: 'jacuzzi' },
    { pattern: /\b(sauna|σάουνα)\b/i, feature: 'sauna' },
    // Parking
    { pattern: /\b(garage|γκαράζ)\b/i, feature: 'garage' },
    { pattern: /\b(covered\s*parking|underground\s*parking)\b/i, feature: 'covered-parking' },
    { pattern: /\b(parking|πάρκινγκ|parking\s*space)(?!\s*underground|\s*covered)/i, feature: 'parking' },
    // Security
    { pattern: /\b(security\s*system|alarm|συναγερμός)\b/i, feature: 'security' },
    { pattern: /\b(cctv|cameras|κάμερες)\b/i, feature: 'cctv' },
    { pattern: /\b(gated\s*community|gated\s*complex)\b/i, feature: 'gated' },
    { pattern: /\b(concierge|doorman)\b/i, feature: 'concierge' },
    // Energy
    { pattern: /\b(solar\s*panel|ηλιακός|photovoltaic)\b/i, feature: 'solar-panels' },
    { pattern: /\b(solar\s*water|solar\s*heater)\b/i, feature: 'solar-water-heater' },
    { pattern: /\b(double\s*glazing|double[\s-]?glazed|διπλά\s*τζάμια)\b/i, feature: 'double-glazing' },
    { pattern: /\b(energy\s*class\s*a|energy\s*efficient)\b/i, feature: 'energy-class-a' },
    // Legal & furnishing
    { pattern: /\b(title\s*deed|τίτλοι|freehold)\b/i, feature: 'title-deeds' },
    { pattern: /\b(fully\s*furnished|επιπλωμένο|turn[\s-]?key)\b/i, feature: 'fully-furnished' },
    { pattern: /\b(partially\s*furnished|semi[\s-]?furnished)\b/i, feature: 'partially-furnished' },
    { pattern: /\b(white\s*goods|appliances)\b/i, feature: 'white-goods' },
    // Location
    { pattern: /\b(beachfront|beach\s*front|seafront)\b/i, feature: 'beachfront' },
    { pattern: /\b(near\s*beach|close\s*to\s*beach)\b/i, feature: 'near-beach' },
    { pattern: /\b(near\s*shops|near\s*amenities)\b/i, feature: 'near-shops' },
    { pattern: /\b(near\s*schools|close\s*to\s*schools)\b/i, feature: 'near-schools' },
    { pattern: /\b(quiet\s*area|peaceful|tranquil)\b/i, feature: 'quiet-area' },
    // Utilities
    { pattern: /\b(pets\s*allowed|pet[\s-]?friendly)\b/i, feature: 'pets-allowed' },
    { pattern: /\b(internet|wifi|wi[\s-]?fi)\b/i, feature: 'internet' },
    { pattern: /\b(bills\s*included|utilities\s*included)\b/i, feature: 'bills-included' },
  ];
  
  for (const { pattern, feature } of featurePatterns) {
    if (pattern.test(lowerText) && !features.includes(feature)) {
      features.push(feature);
    }
  }
  
  return features;
}

// Detect furnished status from text
function detectFurnished(text: string): 'fully' | 'partially' | 'unfurnished' | undefined {
  const lowerText = text.toLowerCase();
  
  // Order matters: check more specific patterns first
  const fullyPatterns = [
    /\bfully\s*furnished\b/i,
    /\bturn[\s-]?key\b/i,
    /\bturnkey\b/i,
    /\bπλήρως\s*επιπλωμένο\b/i,
  ];
  
  const partiallyPatterns = [
    /\bpartially\s*furnished\b/i,
    /\bsemi[\s-]?furnished\b/i,
    /\bpart\s*furnished\b/i,
    /\bμερικώς\s*επιπλωμένο\b/i,
  ];
  
  const unfurnishedPatterns = [
    /\bunfurnished\b/i,
    /\bwithout\s*furniture\b/i,
    /\bempty\b/i,
    /\bχωρίς\s*έπιπλα\b/i,
  ];
  
  // Check fully furnished first
  for (const pattern of fullyPatterns) {
    if (pattern.test(lowerText)) return 'fully';
  }
  
  // Check partially furnished
  for (const pattern of partiallyPatterns) {
    if (pattern.test(lowerText)) return 'partially';
  }
  
  // Check unfurnished
  for (const pattern of unfurnishedPatterns) {
    if (pattern.test(lowerText)) return 'unfurnished';
  }
  
  // Check generic "furnished" only if not preceded by "un" or "semi" or "partially"
  if (/(?<![un])\bfurnished\b/i.test(lowerText) && 
      !/\bun\s*furnished\b/i.test(lowerText) &&
      !/\bsemi\b/i.test(lowerText) &&
      !/\bpartially\b/i.test(lowerText)) {
    return 'fully';
  }
  
  return undefined;
}

// List of CORS proxies to try
const CORS_PROXIES = [
  'https://api.allorigins.win/raw?url=',
  'https://corsproxy.io/?',
  'https://api.codetabs.com/v1/proxy?quest=',
];

async function fetchWithCorsProxy(url: string): Promise<string | null> {
  for (const proxy of CORS_PROXIES) {
    try {
      const response = await fetch(proxy + encodeURIComponent(url), {
        headers: {
          'Accept': 'text/html,application/xhtml+xml',
        },
      });
      
      if (response.ok) {
        return await response.text();
      }
    } catch (err) {
      console.warn(`Proxy ${proxy} failed:`, err);
      continue;
    }
  }
  return null;
}

// Check if URL is from Facebook
function isFacebookUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname.includes('facebook.com') || 
           parsedUrl.hostname.includes('fb.com') ||
           parsedUrl.hostname.includes('marketplace.facebook.com');
  } catch {
    return false;
  }
}

// Scrape using the Supabase edge function (server-side, better for Facebook and sites with bot protection)
async function scrapeWithEdgeFunction(url: string): Promise<ScrapeResult> {
  try {
    console.log('Scraping with edge function:', url);
    
    const { data, error } = await supabase.functions.invoke('scrape-listing', {
      body: { url },
    });

    if (error) {
      console.error('Edge function error:', error);
      return { success: false, error: error.message || 'Failed to scrape listing' };
    }

    if (data?.error) {
      return { success: false, error: data.error };
    }

    if (data?.success && data?.data) {
      console.log('Edge function scraped data:', {
        title: data.data.title,
        hasDescription: !!data.data.description,
        price: data.data.price,
        bedrooms: data.data.bedrooms,
        imageCount: data.data.images?.length,
      });
      return { success: true, data: data.data };
    }

    return { success: false, error: 'No data returned from edge function' };
  } catch (err) {
    console.error('Edge function scrape error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Failed to scrape listing' 
    };
  }
}

// Scrape using CORS proxy (client-side fallback)
async function scrapeWithCorsProxy(url: string): Promise<ScrapeResult> {
  const html = await fetchWithCorsProxy(url);
  
  if (!html) {
    return { success: false, error: 'Could not fetch the page. The website may be blocking our request.' };
  }

  // Extract data from the HTML
  const metaTags = extractMetaTags(html);
  const images = extractImages(html, url);
  // Note: Fallback photo extraction is now handled via edge function in scrapeListingFromUrl
  
  const textContent = extractTextContent(html);

  const result: ScrapedListingData = {
    images,
  };

  // Get data from meta tags
  result.title = metaTags['og:title'] || metaTags['title'];
  result.description = metaTags['og:description'] || metaTags['description'];
  
  // Add OG image
  if (metaTags['og:image']) {
    let ogImage = metaTags['og:image'];
    if (ogImage.startsWith('//')) ogImage = 'https:' + ogImage;
    if (!result.images?.includes(ogImage)) {
      result.images = [ogImage, ...(result.images || [])];
    }
  }

  // Parse from text content - get square meters first for price validation
  result.squareMeters = parseSquareMeters(textContent);
  result.price = parsePrice(textContent, result.squareMeters); // Pass sqm to help detect price/sqm vs total
  result.bedrooms = parseBedrooms(textContent);
  result.bathrooms = parseBathrooms(textContent);
  result.listingType = detectListingType(textContent);
  result.condition = detectCondition(textContent);
  
  // Detect property type from title first (highest priority), then from full text
  const titleText = result.title || '';
  const propertyTypeFromTitle = detectPropertyType(titleText);
  if (propertyTypeFromTitle) {
    result.propertyType = propertyTypeFromTitle.type;
  } else {
    const propertyTypeResult = detectPropertyType(textContent);
    if (propertyTypeResult) {
      result.propertyType = propertyTypeResult.type;
    }
  }
  
  // Detect features from text content
  result.features = detectFeatures(textContent);
  
  // Detect furnished status
  result.furnished = detectFurnished(textContent);
  
  // Detect city from text content (including title)
  const fullTextForCity = titleText + ' ' + textContent;
  result.city = detectCity(fullTextForCity);

  console.log('CORS proxy scraped data:', {
    title: result.title,
    hasDescription: !!result.description,
    price: result.price,
    bedrooms: result.bedrooms,
    propertyType: result.propertyType,
    condition: result.condition,
    furnished: result.furnished,
    city: result.city,
    featuresCount: result.features?.length,
    imageCount: result.images?.length,
  });

  return { success: true, data: result };
}

/**
 * Call the fallback photo extraction edge function
 * Only called when primary extraction returns 0 images
 */
async function extractPhotosFallback(url: string): Promise<string[]> {
  try {
    console.log('📸 Calling fallback photo extraction edge function...');
    
    const { data, error } = await supabase.functions.invoke('extract-photos-fallback', {
      body: { url },
    });

    if (error) {
      console.error('Fallback photo extraction error:', error);
      return [];
    }

    if (data?.success && data?.images?.length > 0) {
      console.log(`📸 Fallback extraction found ${data.images.length} images`);
      return data.images;
    }

    return [];
  } catch (err) {
    console.error('Failed to call fallback photo extraction:', err);
    return [];
  }
}

/**
 * Call the AI vision extraction edge function
 * Only called when we're missing critical fields like city
 */
async function extractWithVision(url: string, missingFields: string[]): Promise<Partial<ScrapedListingData>> {
  try {
    console.log('🔍 Calling AI vision extraction edge function for missing fields:', missingFields);
    
    const { data, error } = await supabase.functions.invoke('extract-listing-vision', {
      body: { url, missingFields },
    });

    if (error) {
      console.error('Vision extraction error:', error);
      return {};
    }

    if (data?.success && data?.data) {
      console.log('🔍 Vision extraction found:', data.data);
      return data.data;
    }

    return {};
  } catch (err) {
    console.error('Failed to call vision extraction:', err);
    return {};
  }
}

export async function scrapeListingFromUrl(url: string): Promise<ScrapeResult> {
  try {
    // Validate URL
    try {
      new URL(url);
    } catch {
      return { success: false, error: 'Invalid URL format' };
    }

    // For Facebook URLs, return a clear error immediately
    // Facebook blocks all automated scraping attempts
    if (isFacebookUrl(url)) {
      console.log('Detected Facebook URL - cannot scrape automatically');
      return {
        success: false,
        error: 'Facebook blocks automated access. Please copy the listing details manually and enter them in the form.'
      };
    }

     // Validate that the URL is actually a real estate listing before proceeding
     const validation = await validateListingUrl(url);
     
     if (!validation.isRealEstate && validation.confidence !== 'low') {
       console.log('❌ URL is not a real estate listing:', validation.detectedType, validation.reason);
       return {
         success: false,
         error: `This doesn't appear to be a real estate listing. Detected: ${validation.detectedType || 'non-property content'}. ${validation.reason || 'Please provide a URL to a property for sale or rent.'}`,
         isNotRealEstate: true,
         detectedType: validation.detectedType,
       };
     }
     
     if (validation.confidence === 'low') {
       console.log('⚠️ Low confidence in real estate detection, proceeding with caution');
     } else {
       console.log('✅ URL validated as real estate listing');
     }
 
    // For other sites, try edge function first (has AI extraction), fall back to CORS proxy
    console.log('Trying edge function first...');
    const edgeResult = await scrapeWithEdgeFunction(url);
    
    if (edgeResult.success && edgeResult.data) {
      // Check if we got meaningful data
      const hasData = edgeResult.data.title || 
                      edgeResult.data.description || 
                      edgeResult.data.price || 
                      (edgeResult.data.images && edgeResult.data.images.length > 0);
      
      if (hasData) {
        // Check if we need fallback photo extraction - OPTIMIZED
        // Only trigger fallback if we have 0 images (1 image is usually enough to proceed)
        const imageCount = edgeResult.data.images?.length || 0;
        if (imageCount === 0) {
          console.log(`⚠️ No images found, trying fallback extraction...`);
          const fallbackImages = await extractPhotosFallback(url);
          if (fallbackImages.length > imageCount) {
            console.log(`📸 Fallback found ${fallbackImages.length} images (was ${imageCount})`);
            // Merge fallback images with existing, avoiding duplicates
            const existingSet = new Set(edgeResult.data.images || []);
            const newImages = fallbackImages.filter(img => !existingSet.has(img));
            edgeResult.data.images = [...(edgeResult.data.images || []), ...newImages].slice(0, 30);
          }
        }
        
        // Check if we need vision extraction for missing critical fields - OPTIMIZED
        // Only call vision extraction if we're missing BOTH city AND price (most critical)
        const missingFields: string[] = [];
        if (!edgeResult.data.city) missingFields.push('city');
        if (!edgeResult.data.price) missingFields.push('price');
        if (edgeResult.data.bedrooms === undefined) missingFields.push('bedrooms');
        if (!edgeResult.data.propertyType) missingFields.push('propertyType');
        
        // Only trigger expensive vision extraction if we're missing critical fields (city or price)
        const hasCriticalMissing = !edgeResult.data.city || !edgeResult.data.price;
        if (hasCriticalMissing && missingFields.length > 0) {
          console.log('⚠️ Missing fields:', missingFields, '- trying vision extraction...');
          const visionData = await extractWithVision(url, missingFields);
          
          // Merge vision data (only for fields we don't already have)
          if (!edgeResult.data.city && visionData.city) {
            edgeResult.data.city = visionData.city;
          }
          if (!edgeResult.data.price && visionData.price) {
            edgeResult.data.price = visionData.price;
          }
          if (edgeResult.data.bedrooms === undefined && visionData.bedrooms !== undefined) {
            edgeResult.data.bedrooms = visionData.bedrooms;
          }
          if (!edgeResult.data.propertyType && visionData.propertyType) {
            edgeResult.data.propertyType = visionData.propertyType;
          }
          if (!edgeResult.data.bathrooms && visionData.bathrooms) {
            edgeResult.data.bathrooms = visionData.bathrooms;
          }
          if (!edgeResult.data.squareMeters && visionData.squareMeters) {
            edgeResult.data.squareMeters = visionData.squareMeters;
          }
          if (!edgeResult.data.address && visionData.address) {
            edgeResult.data.address = visionData.address;
          }
          if (!edgeResult.data.listingType && visionData.listingType) {
            edgeResult.data.listingType = visionData.listingType;
          }
          if (!edgeResult.data.condition && visionData.condition) {
            edgeResult.data.condition = visionData.condition;
          }
          if (!edgeResult.data.furnished && visionData.furnished) {
            edgeResult.data.furnished = visionData.furnished;
          }
          // Merge features
          if (visionData.features && visionData.features.length > 0) {
            const existingFeatures = new Set(edgeResult.data.features || []);
            for (const feature of visionData.features) {
              existingFeatures.add(feature);
            }
            edgeResult.data.features = Array.from(existingFeatures);
          }
        }
        
        return edgeResult;
      }
    }

    // Fall back to CORS proxy for simpler sites
    console.log('Edge function returned limited data, trying CORS proxy...');
    const corsResult = await scrapeWithCorsProxy(url);
    
    // Apply fallbacks to CORS result too
    if (corsResult.success && corsResult.data) {
      // Check if we need fallback photo extraction - OPTIMIZED (only if 0 images)
      const corsImageCount = corsResult.data.images?.length || 0;
      if (corsImageCount === 0) {
        console.log(`⚠️ CORS: No images found, trying fallback extraction...`);
        const fallbackImages = await extractPhotosFallback(url);
        if (fallbackImages.length > corsImageCount) {
          const existingSet = new Set(corsResult.data.images || []);
          const newImages = fallbackImages.filter(img => !existingSet.has(img));
          corsResult.data.images = [...(corsResult.data.images || []), ...newImages].slice(0, 30);
        }
      }
      
      // Check if we need vision extraction - OPTIMIZED (only if missing city or price)
      const missingFields: string[] = [];
      if (!corsResult.data.city) missingFields.push('city');
      if (!corsResult.data.price) missingFields.push('price');
      if (corsResult.data.bedrooms === undefined) missingFields.push('bedrooms');
      if (!corsResult.data.propertyType) missingFields.push('propertyType');
      
      const hasCriticalMissing = !corsResult.data.city || !corsResult.data.price;
      if (hasCriticalMissing && missingFields.length > 0) {
        console.log('⚠️ CORS: Missing fields:', missingFields, '- trying vision extraction...');
        const visionData = await extractWithVision(url, missingFields);
        
        // Merge vision data
        if (!corsResult.data.city && visionData.city) {
          corsResult.data.city = visionData.city;
        }
        if (!corsResult.data.price && visionData.price) {
          corsResult.data.price = visionData.price;
        }
        if (corsResult.data.bedrooms === undefined && visionData.bedrooms !== undefined) {
          corsResult.data.bedrooms = visionData.bedrooms;
        }
        if (!corsResult.data.propertyType && visionData.propertyType) {
          corsResult.data.propertyType = visionData.propertyType;
        }
        if (!corsResult.data.bathrooms && visionData.bathrooms) {
          corsResult.data.bathrooms = visionData.bathrooms;
        }
        if (!corsResult.data.squareMeters && visionData.squareMeters) {
          corsResult.data.squareMeters = visionData.squareMeters;
        }
      }
    }
    
    // Return whichever has more data
    if (corsResult.success && corsResult.data) {
      const edgeDataCount = edgeResult.data ? 
        Object.values(edgeResult.data).filter(v => v !== undefined && v !== null && v !== '').length : 0;
      const corsDataCount = corsResult.data ? 
        Object.values(corsResult.data).filter(v => v !== undefined && v !== null && v !== '').length : 0;
      
      if (corsDataCount > edgeDataCount) {
        return corsResult;
      }
    }

    // Return edge result if we have any data, otherwise cors result
    return edgeResult.success ? edgeResult : corsResult;

  } catch (err) {
    console.error('Scrape error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Failed to scrape listing' 
    };
  }
}

// Convert image URLs to File objects for photo upload
// Uses server-side download first (bypasses CORS), falls back to client-side
export const DEFAULT_MAX_IMPORT_PHOTOS = 10;
export const EXTENDED_MAX_IMPORT_PHOTOS = 20;
export const MAX_IMPORT_PHOTOS = DEFAULT_MAX_IMPORT_PHOTOS;

export interface DownloadImagesResult {
  files: { file: File; preview: string }[];
  totalFound: number;
  wasTruncated: boolean;
}

/**
 * Download images using server-side edge function first (bypasses CORS)
 * Falls back to client-side download for any images that fail server-side
 */
export async function downloadImagesAsFiles(
  imageUrls: string[],
  sourceUrl?: string,
  maxPhotos: number = MAX_IMPORT_PHOTOS
): Promise<DownloadImagesResult> {
  const results: { file: File; preview: string }[] = [];
  const totalFound = imageUrls.length;
  const wasTruncated = totalFound > maxPhotos;
  
  // Limit to maxPhotos images
  const urlsToDownload = imageUrls.slice(0, maxPhotos);
  
  console.log(`📥 Downloading ${urlsToDownload.length} images (of ${totalFound} found)...`);
  
  // First, try server-side download (bypasses CORS/hotlink protection)
  const serverDownloadedUrls = new Set<string>();
  let serverFailedUrls: string[] = [];
  try {
    console.log('Trying server-side download...');
    const { data, error } = await supabase.functions.invoke('download-images', {
      body: { 
        imageUrls: urlsToDownload, 
        maxImages: maxPhotos,
        sourceUrl 
      },
    });
    
    if (!error && data?.images) {
      for (const img of data.images) {
        if (img.success && img.base64) {
          try {
            const byteCharacters = atob(img.base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            
            const extension = img.contentType?.includes('png') ? 'png' : 
                             img.contentType?.includes('webp') ? 'webp' : 'jpg';
            const fileName = `imported-photo-${results.length + 1}.${extension}`;
            const file = new File([byteArray], fileName, { type: img.contentType || 'image/jpeg' });
            const preview = URL.createObjectURL(file);
            
            results.push({ file, preview });
            serverDownloadedUrls.add(img.url);
            console.log(`✅ Server downloaded: ${img.size} bytes`);
          } catch (e) {
            console.warn('Failed to convert server image:', e);
          }
        }
      }
      
      // Track URLs that need client-side download
      if (data.needsClientDownload) {
        serverFailedUrls = data.needsClientDownload;
      }
      
      console.log(`Server-side download: ${results.length}/${urlsToDownload.length} successful`);
      
      // If we got enough images from standard download, return early
      if (results.length >= maxPhotos * 0.7) {
        return { files: results, totalFound, wasTruncated };
      }
    }
  } catch (e) {
    console.warn('Server-side download failed, will try client-side:', e);
  }
  
  // Client-side download for any remaining images
  const remainingUrls = urlsToDownload.filter(url => !serverDownloadedUrls.has(url));
  console.log(`Trying client-side download for ${remainingUrls.length} remaining images...`);
  
  const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeoutMs: number = 8000): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  };
  
  const clientDownloadedUrls = new Set<string>();
  
  for (let i = 0; i < remainingUrls.length && results.length < maxPhotos; i++) {
    const url = remainingUrls[i];
    try {
      let blob: Blob | null = null;
      
      // Try direct fetch first
      try {
        const response = await fetchWithTimeout(url, { 
          mode: 'cors',
          headers: {
            'Accept': 'image/webp,image/apng,image/*,*/*;q=0.8',
          }
        }, 5000);
        if (response.ok) {
          blob = await response.blob();
        }
      } catch {
        // Direct fetch failed, try proxies
      }
      
      // If direct fetch failed, try CORS proxies
      if (!blob) {
        const proxies = [
          'https://api.allorigins.win/raw?url=',
          'https://corsproxy.io/?',
          'https://api.codetabs.com/v1/proxy?quest=',
        ];
        
        for (const proxy of proxies) {
          try {
            const response = await fetchWithTimeout(proxy + encodeURIComponent(url), {
              headers: {
                'Accept': 'image/webp,image/apng,image/*,*/*;q=0.8',
              },
            }, 8000);
            if (response.ok) {
              blob = await response.blob();
              break;
            }
          } catch {
            continue;
          }
        }
      }
      
      if (!blob) continue;
      
      // Skip very small images (likely icons)
      if (blob.size < 5000) continue;
      
      const contentType = blob.type || 'image/jpeg';
      const extension = contentType.includes('png') ? 'png' : 
                       contentType.includes('webp') ? 'webp' : 'jpg';
      const fileName = `imported-photo-${results.length + 1}.${extension}`;
      const file = new File([blob], fileName, { type: contentType });
      const preview = URL.createObjectURL(file);
      
      results.push({ file, preview });
      clientDownloadedUrls.add(url);
      console.log(`✅ Client downloaded image ${results.length}: ${blob.size} bytes`);
    } catch (err) {
      console.warn(`Failed to download image ${i}:`, err);
    }
  }
  
  // Check if we still have too few images - trigger Firecrawl fallback
  const allDownloadedUrls = new Set([...serverDownloadedUrls, ...clientDownloadedUrls]);
  const stillFailedUrls = urlsToDownload.filter(url => !allDownloadedUrls.has(url));
  
  if (results.length < Math.min(urlsToDownload.length * 0.3, 3) && stillFailedUrls.length > 0) {
    console.log(`🔄 Only ${results.length} images downloaded, ${stillFailedUrls.length} still failed. Trying Firecrawl fallback...`);
    
    try {
      const { data, error } = await supabase.functions.invoke('download-images', {
        body: { 
          imageUrls: stillFailedUrls.slice(0, 10), // Cap at 10 for cost control
          maxImages: Math.min(stillFailedUrls.length, 10),
          sourceUrl,
          useFirecrawlFallback: true,
        },
      });
      
      if (!error && data?.images) {
        for (const img of data.images) {
          if (img.success && img.base64 && results.length < maxPhotos) {
            try {
              const byteCharacters = atob(img.base64);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              
              const extension = img.contentType?.includes('png') ? 'png' : 
                               img.contentType?.includes('webp') ? 'webp' : 'jpg';
              const fileName = `imported-photo-${results.length + 1}.${extension}`;
              const file = new File([byteArray], fileName, { type: img.contentType || 'image/jpeg' });
              const preview = URL.createObjectURL(file);
              
              results.push({ file, preview });
              console.log(`✅ Firecrawl fallback downloaded: ${img.size} bytes`);
            } catch (e) {
              console.warn('Failed to convert Firecrawl image:', e);
            }
          }
        }
        console.log(`🔄 Firecrawl fallback: ${data.images?.filter((i: any) => i.success).length || 0} additional images recovered`);
      }
    } catch (e) {
      console.warn('Firecrawl fallback call failed:', e);
    }
  }
  
  console.log(`📥 Download complete: ${results.length} images`);
  
  return {
    files: results,
    totalFound,
    wasTruncated,
  };
}
